import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { CardIcon, LoaderDots, ModalContent, ModalHeader, ModalLayout, ModalTitle } from 'components';
import { theme, toRem } from 'design';
import { VGSCollectPaymentMethodForm } from 'forms/VGSCollectPaymentMethodForm/VGSCollectPaymentMethodForm';
import { clearRequest, selectAPIRequestErrors, selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { App, destroyCardSession, getCardSessionRequest, selectCardSession } from 'store/App';
import { Card } from 'store/Card';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, RequestStatus } from 'types';
import { ErrorModal } from './ErrorModal';

interface IPaymentMethodProps {
  onClose: () => void;
  onSuccessToast: () => void;
  hasPaymentMethod?: boolean;
}

interface ICreateCardError {
  title: string;
  message: string;
}

// Shown when the card session request fails.
export const CARD_SESSION_ERROR: ICreateCardError = {
  title: 'Sorry, there was an error!',
  message: 'Please refresh and try again. If this problem persists, please contact Kickfin.',
};

// Maps a tokenization request failure to the copy shown in the error modal.
export const getTokenizationError = (statusCode: string): ICreateCardError => {
  if (statusCode === '429') {
    return {
      title: 'Maximum number of attempts reached!',
      message: 'Sorry, but you have reached the maximum attempts allowed. Please close the window and try again.',
    };
  }
  return {
    title: 'Sorry, there was an error!',
    message: 'We are having trouble adding your card at this time. Please try again later.',
  };
};

const ModalDescText = styled.div`
  margin: ${toRem(theme.space.sm)} 0 ${toRem(theme.space.lg)};
  font-size: ${toRem(theme.font.size.md)};
  text-align: center;
`;

const PaymentMethodCardIcon = styled(CardIcon)`
  width: ${toRem(34)};
  height: ${toRem(22)};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: 2px;

  &:after {
    display: none;
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const PaymentMethodCardIconsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  & > * {
    margin: 0 ${toRem(theme.space.sm)};
  }
`;

const PaymentMethod: React.FC<IPaymentMethodProps> = ({ onClose, hasPaymentMethod, onSuccessToast }) => {
  const dispatch = useDispatch();
  const pluginInitialized = useRef(false);
  const getCardSessionError = useSelector((state: IStore) => selectAPIRequestErrors(state, App.GET_CARD_SESSION_REQUEST)) as IApiErrorPayloadWithCode;
  const addCardStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Card.CREATE_TOKENIZED_CARD_REQUEST));
  const cardSession = useSelector(selectCardSession());
  const isPluginLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, App.GET_CARD_SESSION_REQUEST));
  const [createTokenizedCardError, setCreateTokenizedCardError] = useState<ICreateCardError>(null);

  useEffect(() => {
    dispatch(destroyCardSession());
  }, []);

  useEffect(() => {
    const onSuccessModalConfirm = () => {
      dispatch(clearRequest(Card.CREATE_TOKENIZED_CARD_REQUEST));
      dispatch(destroyCardSession());
      onClose();
      onSuccessToast();
    };

    if (addCardStatus === RequestStatus.SUCCESS) onSuccessModalConfirm();
  }, [addCardStatus, dispatch, onSuccessToast, onClose]);

  const initializePlugin = useCallback(() => {
    if (pluginInitialized.current) return;

    dispatch(getCardSessionRequest(window.location.origin));
    pluginInitialized.current = true;
  }, []);

  useEffect(() => {
    if (!cardSession?.sessionId && !isPluginLoading) {
      initializePlugin();
    }
  }, [cardSession, isPluginLoading]);

  const closeModal = useCallback(() => {
    setCreateTokenizedCardError(null);
    dispatch(clearRequest(Card.CREATE_TOKENIZED_CARD_REQUEST));
    dispatch(destroyCardSession());
    onClose?.();
  }, [onClose]);

  const onPaymentMethodError = useCallback((error: IApiErrorPayloadWithCode) => {
    setCreateTokenizedCardError(getTokenizationError(error.statusCode));
  }, []);

  if (getCardSessionError?.statusCode || createTokenizedCardError) {
    const { title, message } = createTokenizedCardError || CARD_SESSION_ERROR;
    return <ErrorModal isActive title={title} message={message} onClose={closeModal} />;
  }

  return (
    <ModalLayout maxWidth={540} isActive onClose={closeModal}>
      <ModalHeader>
        <ModalTitle>{`${hasPaymentMethod ? 'Edit' : 'Add'} Payment Method`}</ModalTitle>
        <ModalDescText>Please enter your debit card information</ModalDescText>
        <PaymentMethodCardIconsContainer>
          <PaymentMethodCardIcon icon="DiscoverCard" />
          <PaymentMethodCardIcon icon="Visa" />
          <PaymentMethodCardIcon icon="Mastercard" />
          <PaymentMethodCardIcon icon="AmericanExpress" />
        </PaymentMethodCardIconsContainer>
      </ModalHeader>
      <ModalContent>{!pluginInitialized.current && !cardSession?.sessionId ? <LoaderDots /> : <VGSCollectPaymentMethodForm onError={onPaymentMethodError} />}</ModalContent>
    </ModalLayout>
  );
};

export default PaymentMethod;
